import { useEffect, useRef, useState } from 'react';
import { useStore } from './useStore';
import api from '../api/api';
import KioAppHelper from '../helpers/kioApp';
import passKeyHelper from '../helpers/passKey';
import { KioskState } from '../typegen/kiosk-state';
import logger from '../helpers/logger';
import sqlite from '../helpers/sqlite';
import { describeRequestError } from '../helpers/requestError';
import { store } from '../stores/store';

let isRunning = false;

const isHealthCheckDisabled = () => !!store.sessionStore.sessionId;

export default function useSystemAvailability() {
	const {
		configStore: { isConfigLoaded, companyId },
		kioskStore: { legacyIsApiActive, kioskId, lastKioskStateOnServer, updateKioskStateOnServer },
		sessionStore: { sessionId },
		uiStore: { showLoader, hideLoader },
	} = useStore();
	const [state, setState] = useState<KioskState>();

	const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const POLL_INTERVAL_MS = 30000; // 30 seconds

	// Once a customer starts using the kiosk, checks stay off until the page reloads. A check cut off by the end-of-visit reload reads as Unavailable.
	const healthCheckDisabled = !!sessionId;

	useEffect(() => {
		if (state === undefined && lastKioskStateOnServer !== undefined) setState(lastKioskStateOnServer);
	}, [lastKioskStateOnServer, state]);

	useEffect(() => {
		const handleUpdateServerState = async (state: KioskState) => {
			if (isHealthCheckDisabled()) return;
			try {
				if (lastKioskStateOnServer !== state) await updateKioskStateOnServer(state);
			} catch (error) {
				logger.logGeneral.error({}, 'Error updating kiosk state on server:', error);
			}
		};

		const applyState = (next: KioskState) => {
			if (isHealthCheckDisabled()) {
				KioAppHelper('LogInfo', `[Availability] Ignored late ${KioskState[next]} result, health check is disabled`);
				return;
			}
			setState(next);
		};

		const getKioskState = async () => {
			if (state === undefined || isRunning || isHealthCheckDisabled()) return;
			isRunning = true;

			setState(lastKioskStateOnServer);

			if (KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_FORCE_UNAVAILABLE) === 'true') {
				setState(KioskState.Unavailable);
				await handleUpdateServerState(KioskState.Unavailable);
				isRunning = false;
				return;
			}

			const stateIsUnknown = lastKioskStateOnServer === KioskState.Unknown;

			if (stateIsUnknown) showLoader();

			try {
				const cashBoxAlarmTriggered = (await sqlite.getSetting('cashBoxAlarmTriggered')) === 'true';
				if (cashBoxAlarmTriggered) {
					applyState(KioskState.CashBoxAlarm);
					await handleUpdateServerState(KioskState.CashBoxAlarm);
					isRunning = false;
					return;
				}

				if (lastKioskStateOnServer === KioskState.OutOfService) {
					isRunning = false;
					return;
				}

				const startedAt = Date.now();
				const [legacyApi, rbpkApi] = await Promise.all([legacyIsApiActive(), api.kiosks.isAvailable()]);
				if (!legacyApi) logger.logGeneral.warn({}, 'Legacy BPK Admin API is not active');
				if (!rbpkApi) logger.logGeneral.warn({}, 'RBPK Kiosk API is not active');
				const isBothApisEnabled = !!legacyApi && !!rbpkApi;

				// If either API is unavailable, mark system as unavailable
				if (!isBothApisEnabled) {
					KioAppHelper(
						'LogWarn',
						`[Availability] Check failed: legacy=${legacyApi ? 'ok' : 'failed'} rbpk=${rbpkApi ? 'ok' : 'failed'} after ${Date.now() - startedAt} ms`
					);
					applyState(KioskState.Unavailable);
					await handleUpdateServerState(KioskState.Unavailable);
					isRunning = false;
					return;
				}

				const passKey = await passKeyHelper.generatePassKey();
				const isMaintenanceModeOn = await api.legacyBpkAdmin.getIsMaintenanceModeOn({
					kioskID: kioskId,
					customerID: companyId,
					passKey,
				});

				// Set state based on maintenance mode
				applyState(isMaintenanceModeOn ? KioskState.Maintenance : KioskState.Active);
			} catch (error) {
				KioAppHelper('LogWarn', `[Availability] Check error (${describeRequestError(error)})`);
				applyState(KioskState.Unavailable);
				logger.logGeneral.error({}, 'Error checking system availability:', error);
			} finally {
				await handleUpdateServerState(state);
				if (stateIsUnknown) hideLoader();
				isRunning = false;
			}
		};

		if (isConfigLoaded && !healthCheckDisabled) {
			getKioskState();
			pollerRef.current = setInterval(getKioskState, POLL_INTERVAL_MS);
		}

		return () => {
			if (pollerRef.current) {
				clearInterval(pollerRef.current);
				pollerRef.current = null;
			}
		};
	}, [
		isConfigLoaded,
		healthCheckDisabled,
		legacyIsApiActive,
		kioskId,
		companyId,
		lastKioskStateOnServer,
		showLoader,
		hideLoader,
		updateKioskStateOnServer,
		state,
	]);

	return state;
}
